/**
 * Screenplay-location name rules — ONE definition, shared by every site that has to agree.
 *
 * These mirror Unity's `DatabaseManager.GetCanonicalScreenplayLocationName` /
 * `GetNormalizedScreenplayLocationKey`, and they are load-bearing in two places:
 *   - `locationGrouping`, on UN-MIGRATED (v41-51) files only, groups a scene under its FK while
 *     `normalizeLocationKey(location.name) === normalizeLocationKey(scene.name)`. 20260923 - on v52+
 *     files the FK alone decides grouping: `scenes.name` is the scene's OWN location text (owner: merging
 *     must not strip "(OLD JOE MEMORY)" — "it won't match things like printed scripts"), so it may carry
 *     an extra after the row name. The boundary-prefix helpers at the bottom of this file own that rule;
 *   - the Locations tab's rename must canonicalize exactly as Unity will, or the optimistic mirror
 *     disagrees with the .previs until the next repush.
 *
 * The rule was duplicated across FOUR files before this module — locationGrouping, BrowseMode,
 * projectStore (twice: rename and addScreenplayLocation) and SceneSettingsModal. Every one of them
 * now imports from here: SceneSettingsModal's last two private copies (`normLocation` and the inline
 * canonicalize inside `confirmAddLocation`) were folded in on 2026-08-25, when the Script tab grew an
 * Add Location popup of its own and a second normalizer would have meant three sites agreeing by
 * hand. There is NO remaining copy — a new one would let one entry point mint a duplicate row for a
 * location every other view considers identical.
 *
 * Drift here does not fail a test — it quietly fragments the Locations tab.
 *
 * KNOWN, ACCEPTED divergence from Unity: JS `toUpperCase()` and .NET `ToUpperInvariant()` disagree on
 * a few exotic inputs (eszett, NEL, BOM). Bounded and deliberately not papered over here — a location
 * name that differs only by such a character is not a real-world case, and "fixing" it web-side would
 * make the two sides disagree in a *different* way. Since the canonical form is now force-uppercased
 * (not just the identity key), that divergence reaches the STORED name: a web-typed "Straße" stores as
 * "STRASSE" on both sides (the web sends the already-uppercased string and Unity's uppercase is
 * idempotent over it), while the same name typed on a Unity surface stores as "STRAßE".
 */

/** Stored/display form: trimmed, internal whitespace runs collapsed, FORCE-UPPERCASED — a hand-typed
 *  name must read like a parsed slugline, which the importer already uppercases. */
export function canonicalizeLocationName(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toUpperCase();
}

/** Identity form: the canonical name uppercased. Two locations with the same key ARE the same location. */
export function normalizeLocationKey(name: string): string {
  return canonicalizeLocationName(name).toUpperCase();
}

/**
 * Name for a duplicate of `sourceName`: the full source name plus "-N", where N is the lowest integer
 * >= 2 whose identity is free among `existingNames`.
 *
 * The suffix is APPENDED to the whole current name — existing suffixes are never parsed, so
 * "TERMINAL-2" duplicates to "TERMINAL-2-2". Predictable beats clever: suffix parsing would have to
 * guess whether "-2" in a user's own name is a duplicate marker or part of the location ("STAGE-2"),
 * and guessing wrong silently renames someone's set.
 *
 * Returns the canonical form, so the caller can send it straight to Unity.
 */
export function nextDuplicateLocationName(sourceName: string, existingNames: string[]): string {
  const base = canonicalizeLocationName(sourceName);
  const taken = new Set(existingNames.map(normalizeLocationKey));

  // Bounded by construction: each miss consumes one taken key, so this cannot outrun the list.
  for (let suffix = 2; ; suffix++) {
    const candidate = `${base}-${suffix}`;
    if (!taken.has(normalizeLocationKey(candidate))) return candidate;
  }
}

// ── 20260923 - a scene keeps its own location text; the location row decides grouping ──
//
// Owner (2026-09-23): "Is there a way for us to preserve that additional text (OLD JOE MEMORY),
// - MOMENTS LATER, - CONTINUOUS, etc. on merge such-that for the scene row ... it continues to show the
// full BARN (OLD JOE MEMORY) text (and the user can press the pencil button to edit JUST that extra text".
// So a scene linked to BARN may read "BARN (OLD JOE MEMORY)". These three helpers are the ONE web-side
// definition of how a scene's text relates to its row's name. Unity mirrors them in DatabaseManager (the
// boundary-prefix check used by RelinkSceneLocationCore's stability rule and RenameScreenplayLocationCore's
// per-scene prefix swap) — change one side and the other must follow, or a rename mirrored here would
// disagree with the .previs until the next repush.

/** A letter or digit in any script — the web twin of .NET `char.IsLetterOrDigit`, which Unity's side of
 *  the boundary check uses. Anything else (space, "(", "-", ",", "/", "'") is a boundary. */
const LETTER_OR_DIGIT = /[\p{L}\p{N}]/u;

/** Length of `rowName`'s key as a prefix of `text`'s key, or -1 when `text` does not start with it.
 *  Both keys are the canonical form (trimmed, collapsed, uppercased), so the returned length indexes
 *  straight into `canonicalizeLocationName(text)`. */
function locationPrefixLength(text: string, rowName: string): number {
  const textKey = normalizeLocationKey(text);
  const rowKey = normalizeLocationKey(rowName);
  // An empty row name would "prefix" every text — that is no location, not a match.
  if (!rowKey || !textKey.startsWith(rowKey)) return -1;
  if (textKey.length === rowKey.length) return rowKey.length;
  // The boundary: "BARNYARD" is a different place from "BARN", "BARN (OLD JOE MEMORY)" is not.
  return LETTER_OR_DIGIT.test(textKey.charAt(rowKey.length)) ? -1 : rowKey.length;
}

/** True when the scene text `text` "starts with" the location row name `rowName`: the keys are equal,
 *  or `text`'s key starts with `rowName`'s key followed by a non-letter/digit character. "BARN - CONTINUOUS"
 *  and "barn (old joe memory)" start with "BARN"; "BARNYARD" does not. */
export function sceneTextStartsWithLocation(text: string, rowName: string): boolean {
  return locationPrefixLength(text, rowName) >= 0;
}

/** The scene's own addition to its row name: the canonical text minus the row-name prefix, trimmed —
 *  "(OLD JOE MEMORY)" for "BARN (OLD JOE MEMORY)" under BARN, "- MOMENTS LATER" for "BARN - MOMENTS LATER".
 *  '' when the text IS the row name, and '' when it does not start with it at all (callers that need to
 *  tell those apart ask sceneTextStartsWithLocation first). */
export function sceneLocationExtraText(text: string, rowName: string): string {
  const prefixLength = locationPrefixLength(text, rowName);
  if (prefixLength < 0) return '';
  return canonicalizeLocationName(text).slice(prefixLength).trim();
}

/** A location RENAME's effect on one linked scene's text — the only Locations-tab write besides the
 *  per-scene pencil that changes a scene's text. The text IS the old name → the new name; it starts with
 *  the old name → the new name plus the remainder VERBATIM, separator and spacing included ("BARN (OLD JOE
 *  MEMORY)" → "RED BARN (OLD JOE MEMORY)", "BARN - CONTINUOUS" → "RED BARN - CONTINUOUS"); anything else
 *  (a merged-in "HAYLOFT", a hand-edited text) is returned UNTOUCHED — the rename is the row's, not a
 *  licence to rewrite a heading that never carried its name. Unity's RenameScreenplayLocationCore makes
 *  the same three-way decision per scene. */
export function renameLocationInSceneText(text: string, oldName: string, newName: string): string {
  const prefixLength = locationPrefixLength(text, oldName);
  if (prefixLength < 0) return text;
  return canonicalizeLocationName(newName) + canonicalizeLocationName(text).slice(prefixLength);
}
